import {
  Box,
  Flex,
  NumberInput,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
  TextProps,
} from '@chakra-ui/react';
import styled from '@emotion/styled';
import { Code } from 'constants/keyboard';
import { ComponentProps, KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { normalizeNumber } from 'utils/number';

import { NumberInputField } from './NumberInputField';

/**
 * SLIDER + NUMBER INPUT COMPONENT
 *
 * Renders a row containing a title, slider, and
 * number input.
 */
interface SliderNumberInputProps {
  title: string;
  value: number;
  onChange?: (value: number) => void;
  onChangeEnd?: (value: number) => void;

  minValue?: number;
  maxValue?: number;
  step?: number;
  precision?: number;
  suffix?: string;
  fontSize?: string;
  fontWeight?: TextProps['fontWeight'];
  sliderWidth?: string | number;
  disabled?: boolean;
  gap?: number;
  sliderBackground?: string;
  sliderTrackBackground?: string;

  // Custom styling for title and number input elements
  titleProps?: ComponentProps<typeof Text>;
  numberInputProps?: ComponentProps<typeof NumberInput>;
  containerProps?: ComponentProps<typeof Box>;
}

const computeNormalizedNumberFromString = (min: number, max: number, value: string, precision: number) => {
  let normalized = normalizeNumber(min, max, parseFloat(value)).toFixed(precision);
  return Number.isNaN(parseFloat(normalized)) ? 0 : parseFloat(normalized);
};

export const SliderNumberInput = ({
  fontSize = 'sm',
  fontWeight = 'medium',
  title,
  value,
  onChange,
  onChangeEnd,
  sliderWidth = '55%',
  suffix = '',
  minValue = 0,
  maxValue = 100,
  step = 1,
  precision = 1,
  disabled = false,
  gap = 3,
  sliderTrackBackground,
  sliderBackground,
  titleProps,
  numberInputProps,
  containerProps,
}: SliderNumberInputProps) => {
  const [numberInputValue, setNumberInputValue] = useState<string>(`${value}`);
  const numberInputRef = useRef<HTMLInputElement>(null);
  const labelId = useId();

  useEffect(() => {
    setNumberInputValue(`${value}`);
  }, [value]);

  const onNumberInputBlur = () => {
    const parsedNumber = computeNormalizedNumberFromString(minValue, maxValue, numberInputValue, precision);
    onChange?.(parsedNumber);
  };

  const onNumberInputChange = (valueAsString: string, valueAsNumber: number) => {
    setNumberInputValue(valueAsString);
  };

  const formatNumberInput = (value: string | number) => `${value}${suffix}`;

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const key = (e.key || e.code) as Code;

    if (key === Code.Enter) {
      onNumberInputBlur();
    }
  };

  return (
    <Box w='100%' {...containerProps}>
      <Flex mb={1} alignItems={'center'} gap={gap} justifyContent={'space-between'} color={'font.500'}>
        <Text htmlFor={labelId} as={'label'} fontSize={fontSize} fontWeight={fontWeight} {...titleProps}>
          {title}
        </Text>
        <Flex gap={2} justifyContent={'flex-end'} alignItems={'center'}>
          <Slider
            w={sliderWidth}
            aria-label={title}
            onChange={onChange}
            onChangeEnd={onChangeEnd}
            value={value}
            min={minValue}
            max={maxValue}
            step={step}
            focusThumbOnChange={false}
            marginRight='10px'
            isDisabled={disabled}
          >
            <SliderTrack background={sliderTrackBackground}>
              {minValue < 0 ? (
                <StyledNegativeSliderFilledTrack
                  min={minValue}
                  max={maxValue}
                  value={value}
                  background={sliderBackground}
                />
              ) : (
                <SliderFilledTrack />
              )}
            </SliderTrack>
            <SliderThumb />
          </Slider>
          <NumberInput
            size={'sm'}
            max={maxValue}
            min={minValue}
            w='3.5rem'
            step={step}
            precision={precision}
            onChange={onNumberInputChange}
            onBlur={onNumberInputBlur}
            value={numberInputValue}
            format={formatNumberInput}
            {...numberInputProps}
          >
            <NumberInputField
              ref={numberInputRef}
              onKeyDown={onKeyDown}
              p={1}
              m={0}
              border={'1px solid'}
              borderColor={'outline.gray'}
              borderRadius={4}
              data-testid={'slider-number-input'}
              textAlign={'center'}
              h={7}
              disabled={disabled}
              id={labelId}
            />
          </NumberInput>
        </Flex>
      </Flex>
    </Box>
  );
};

// For sliders with negative values:
// We need to override the default SliderFilledTrack
// So that the fill should start from the center of the slider and extend to the left or right in either direction
const StyledNegativeSliderFilledTrack = styled(SliderFilledTrack)((props) => {
  // Find where the value 0 lies as a percentage between min and max.
  const zeroPercentage = ((0 - props.min) / (props.max - props.min)) * 100;

  // Find where the current value lies as a percentage between min and max.
  const valuePercentage = ((props.value - props.min) / (props.max - props.min)) * 100;

  const width = Math.abs(valuePercentage - zeroPercentage);
  const left = valuePercentage < zeroPercentage ? valuePercentage : zeroPercentage;

  return {
    // override default SliderFilledTrack styling
    left: `${left}% !important`,
    width: `${width}% !important`,
  };
});
